"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, RadioGroup, RadioGroupItem, RadioCard, Badge, Section } from "@/src/components";
import { CreditCard, Wallet, Banknote, Mail, Phone, MessageCircle } from "lucide-react";
import { InstallCommand } from "@/components/docs/install-command";

export default function DocsComponentsRadioGroup() {
  const [value, setValue] = React.useState("option-1");

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Radio Group
            </Text>
            <Badge variant="secondary">Form</Badge>
          </div>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            A set of radio buttons for single-selection scenarios. Built on Radix UI
            with support for labels, descriptions, and card variants.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/radio-group" />
        </SectionCard>
      </Section>

      {/* Basic */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Basic Usage</CardTitle>
            <CardDescription>Simple radio group</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <RadioGroup value={value} onValueChange={setValue}>
          <RadioGroupItem value="option-1" label="Option 1" />
          <RadioGroupItem value="option-2" label="Option 2" />
          <RadioGroupItem value="option-3" label="Option 3" />
        </RadioGroup>
        <Text className="text-sm text-muted-foreground mt-4">
          Selected: {value}
        </Text>
      </SectionCard>
      </Section>

      {/* With Legend */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>With Legend</CardTitle>
            <CardDescription>Grouped with fieldset legend</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <RadioGroup
          legend="Select your plan"
          description="Choose the plan that works best for you"
          defaultValue="basic"
        >
          <RadioGroupItem value="free" label="Free" description="$0/month" />
          <RadioGroupItem value="basic" label="Basic" description="$9/month" />
          <RadioGroupItem value="pro" label="Pro" description="$19/month" />
          <RadioGroupItem value="enterprise" label="Enterprise" description="Custom pricing" />
        </RadioGroup>
      </SectionCard>
      </Section>

      {/* Horizontal */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Horizontal Orientation</CardTitle>
            <CardDescription>Radio buttons in a row</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <RadioGroup
          legend="Billing cycle"
          orientation="horizontal"
          defaultValue="monthly"
        >
          <RadioGroupItem value="monthly" label="Monthly" />
          <RadioGroupItem value="quarterly" label="Quarterly" />
          <RadioGroupItem value="yearly" label="Yearly" />
        </RadioGroup>
      </SectionCard>
      </Section>

      {/* Error State */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Error State</CardTitle>
            <CardDescription>Validation error display</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <RadioGroup
          legend="Payment method"
          error="Please select a payment method"
        >
          <RadioGroupItem value="credit" label="Credit Card" />
          <RadioGroupItem value="debit" label="Debit Card" />
          <RadioGroupItem value="paypal" label="PayPal" />
        </RadioGroup>
      </SectionCard>
      </Section>

      {/* Disabled */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Disabled State</CardTitle>
            <CardDescription>Disabled options</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <RadioGroup
          legend="Subscription tier"
          defaultValue="active"
        >
          <RadioGroupItem value="active" label="Active Plan" description="Currently active" />
          <RadioGroupItem value="upgrade" label="Upgrade" description="More features" />
          <RadioGroupItem value="enterprise" label="Enterprise" description="Not available" disabled />
        </RadioGroup>
      </SectionCard>
      </Section>

      {/* Card Variant */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Card Variant</CardTitle>
            <CardDescription>More prominent selection cards</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-8"
      >
        <RadioGroup defaultValue="credit" className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RadioCard
            value="credit"
            title="Credit Card"
            description="Pay with Visa, Mastercard, or Amex"
            icon={<CreditCard className="h-5 w-5" />}
          />
          <RadioCard
            value="wallet"
            title="Digital Wallet"
            description="Apple Pay, Google Pay, or PayPal"
            icon={<Wallet className="h-5 w-5" />}
          />
          <RadioCard
            value="bank"
            title="Bank Transfer"
            description="Direct bank account transfer"
            icon={<Banknote className="h-5 w-5" />}
          />
        </RadioGroup>

        <RadioGroup defaultValue="email" className="grid grid-cols-1 gap-3 max-w-md">
          <RadioCard
            value="email"
            title="Email"
            description="Receive updates via email"
            icon={<Mail className="h-5 w-5" />}
          />
          <RadioCard
            value="phone"
            title="Phone"
            description="Receive SMS notifications"
            icon={<Phone className="h-5 w-5" />}
          />
          <RadioCard
            value="chat"
            title="In-app Chat"
            description="Get notified in the app"
            icon={<MessageCircle className="h-5 w-5" />}
          />
        </RadioGroup>
      </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
        header={<CardTitle>RadioGroup Props</CardTitle>}
        contentClassName="p-6"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium">Prop</th>
                <th className="text-left py-2 pr-4 font-medium">Type</th>
                <th className="text-left py-2 pr-4 font-medium">Default</th>
                <th className="text-left py-2 font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">legend</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 pr-4 text-muted-foreground">-</td>
                <td className="py-2 text-muted-foreground">Group label</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">description</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 pr-4 text-muted-foreground">-</td>
                <td className="py-2 text-muted-foreground">Help text</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">error</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 pr-4 text-muted-foreground">-</td>
                <td className="py-2 text-muted-foreground">Error message</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">orientation</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">&quot;vertical&quot; | &quot;horizontal&quot;</td>
                <td className="py-2 pr-4 text-muted-foreground">&quot;vertical&quot;</td>
                <td className="py-2 text-muted-foreground">Layout direction</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">value</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 pr-4 text-muted-foreground">-</td>
                <td className="py-2 text-muted-foreground">Controlled value</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">onValueChange</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">(value: string) =&gt; void</td>
                <td className="py-2 pr-4 text-muted-foreground">-</td>
                <td className="py-2 text-muted-foreground">Change callback</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>
      </Section>

      {/* RadioCard Props */}
      <Section>
        <SectionCard
        header={<CardTitle>RadioCard Props</CardTitle>}
        contentClassName="p-6"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium">Prop</th>
                <th className="text-left py-2 pr-4 font-medium">Type</th>
                <th className="text-left py-2 font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">title</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 text-muted-foreground">Card title (required)</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">description</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 text-muted-foreground">Card description</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">icon</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 text-muted-foreground">Icon element</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">value</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 text-muted-foreground">Selection value (required)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>
      </Section>
    </>
  );
}
